import { BlossomError } from "@setu/protocol";
import { useCallback, useState } from "react";
import { useSession } from "../identity/SessionProvider";
import { useDeviceSettings } from "../sync/localSettings";
import { DEFAULT_MEDIA_HOST } from "../sync/settingsDocument";
import {
  chooseUploadTarget,
  type UploadProtocol,
  useBlossomPreference,
} from "./blossomPreference";
import { uploadToBlossom } from "./blossomUpload";
import { UploadError, type UploadedMedia, uploadMedia } from "./nip96";

/**
 * The default media host.
 *
 * Re-exported rather than defined here: it is a synced preference now, so the
 * default has to be the same constant the sync layer compares against, and the
 * composer's copy ("attachments go to …") reads it from the same place. Two
 * definitions of "the default" is how a device decides it has unsaved settings
 * forever. Uploading is inherently a choice to trust a third party with a file and a
 * signature, which is why the host is stated in the composer's own copy rather than
 * implied.
 */
export { DEFAULT_MEDIA_HOST };

/** Types we offer to upload. GIF is in the list because GIFs are the point. */
export const ACCEPTED_MEDIA =
  "image/png,image/jpeg,image/gif,image/webp,image/avif,video/mp4,video/webm";

/**
 * A host for a message, without trusting it to be a parseable URL.
 *
 * `new URL(host).hostname` throws on a host the user typed badly — and it would
 * throw *inside the error handler*, replacing "could not reach that server" with an
 * unhandled exception during a failed upload.
 *
 * Exported for the same reason it exists. The composer names the host mid-upload,
 * and a settings document written by another device carries `mediaHost` as an
 * unvalidated string, so the bare `new URL()` this replaced was an unhandled throw
 * *during render* — blanking the composer, with the user's text in it, at the moment
 * their file was in flight.
 */
export function hostLabel(host: string): string {
  try {
    return new URL(host).hostname;
  } catch {
    return host;
  }
}

export type UploadState =
  | { readonly status: "idle" }
  | { readonly status: "uploading"; readonly name: string }
  | { readonly status: "error"; readonly message: string };

export interface UploadApi {
  readonly state: UploadState;
  /**
   * The host this hook will actually upload to.
   *
   * Exposed because it is now a preference that can differ from the default and can
   * arrive from another device. A composer that names the built-in default while
   * uploading somewhere else is telling the reader the wrong thing about where their
   * file went, which is the one detail about an upload they cannot verify afterwards.
   */
  readonly host: string;
  /**
   * Which protocol will carry the next file.
   *
   * Exposed for the same reason `host` is: a file leaves the device and does not come
   * back, so the two facts a user cannot reconstruct afterwards — where it went and
   * how — are the two this hook states rather than implies.
   */
  readonly protocol: UploadProtocol;
  /** Resolves to the media, or undefined when it failed; `state` holds why. */
  upload(file: File): Promise<UploadedMedia | undefined>;
  reset(): void;
}

/**
 * Upload one file and hand back its URL.
 *
 * Requires a signer either way: NIP-98 and Blossom's kind-24242 are both signed
 * events, so a read-only session cannot upload. That is surfaced as a message rather
 * than a silent no-op, because "I pressed attach and nothing happened" is the worst
 * possible reading of a permission problem.
 *
 * Two protocols, one decision, made in `chooseUploadTarget` from state already in
 * memory — the device's NIP-96 host and the mirrored Blossom server list. Nothing on
 * this path waits on a relay: an upload that has to fetch a preference before it
 * knows where to send the file is an upload that lands somewhere else when the fetch
 * is slow.
 *
 * Passing `host` explicitly means NIP-96 at that host, whatever the Blossom
 * preference says. A caller that names a host has named a NIP-96 endpoint; silently
 * routing it elsewhere would make the argument a lie.
 *
 * Both paths return the same `UploadedMedia`, so the composer's `imeta` tags — and
 * therefore how every other client renders the note — do not depend on which one ran.
 */
export function useUpload(host?: string): UploadApi {
  const { session } = useSession();
  const { mediaHost } = useDeviceSettings();
  const preference = useBlossomPreference();
  const [state, setState] = useState<UploadState>({ status: "idle" });

  const target =
    host !== undefined
      ? ({ protocol: "nip96", host } as const)
      : chooseUploadTarget({
          preference,
          pubkey: session?.pubkey,
          mediaHost,
        });
  const protocol = target.protocol;
  const destination = target.host;
  // Read off the preference snapshot rather than the target object, which is rebuilt
  // every render: `useCallback` over an unstable dependency is no memoisation at all.
  const servers = preference.servers;

  const upload = useCallback(
    async (file: File): Promise<UploadedMedia | undefined> => {
      if (!session?.canSign) {
        setState({
          status: "error",
          message: "Uploading needs a signer — unlock or sign in with a key.",
        });
        return undefined;
      }
      setState({ status: "uploading", name: file.name });
      const deps = {
        fetch: (...args: Parameters<typeof globalThis.fetch>) =>
          globalThis.fetch(...args),
        sign: (template: Parameters<typeof session.signer.signEvent>[0]) =>
          session.signer.signEvent(template),
      };
      try {
        const media =
          protocol === "blossom"
            ? await uploadToBlossom({ servers, file }, deps)
            : await uploadMedia({ host: destination, file }, deps);
        setState({ status: "idle" });
        return media;
      } catch (error) {
        setState({
          status: "error",
          // An UploadError or BlossomError already carries a sentence written for a
          // reader; a network failure does not, so it gets one here.
          message:
            error instanceof UploadError || error instanceof BlossomError
              ? error.message
              : `Could not reach ${hostLabel(destination)}. The file was not uploaded.`,
        });
        return undefined;
      }
    },
    [protocol, destination, servers, session],
  );

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return { state, host: destination, protocol, upload, reset };
}
